import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { CircularProgress, Grid, Stack, Tab, Tabs } from "@mui/material";
import MDBox from "components/MDBox";
import { checkPermission } from "utils/HelperFunctions";
import useWorkspaceSubscription from "hooks/useWorkspaceSubscription";
import { PERMISSIONS_NAME } from "lib/constants";
import WinWireSettings from "./WinWireSettings";
import ReportsSettings from "./ReportsSettings";
import InsightsSettings from "./InsightsSettings";

const genTabProps = (index) => ({
  id: `reports-tab-${index}`,
  "aria-controls": `reports-tabpanel-${index}`,
  key: `reports-tab-${index}`,
});

const SIDE_NAV_ITEMS = [
  {
    label: "Reports",
    href: "reports",
    permissionName: PERMISSIONS_NAME.MANAGE_INSIGHTS,
  },
  {
    label: "Insights",
    href: "insights",
    permissionName: PERMISSIONS_NAME.MANAGE_INSIGHTS,
  },
  {
    label: "Win-Wire",
    href: "winwire",
    permissionName: PERMISSIONS_NAME.MANAGE_INSIGHTS,
  },
];

const INSIGHTS_CONFIG_TYPE = [
  {
    label: "Company Settings",
    value: "company",
  },
  {
    label: "My Settings",
    value: "user",
  },
];

function HubSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { subscription } = useWorkspaceSubscription();
  const companyAdmin = useSelector((state) => state.loginUser.loginUserState);
  const [isRequestLoading, setIsRequestLoading] = useState(true);

  const [sidenavItems, setSideNavItems] = useState(SIDE_NAV_ITEMS);
  const [selectedCategory, setSelectedCategory] = useState(
    new URLSearchParams(searchParams).get("subTab")
  );

  const [selectedInsightsConfigType, setSelectedInsightsConfigType] = useState(
    INSIGHTS_CONFIG_TYPE[0].value
  );

  const handleChange = (href) => {
    setSearchParams({
      selectedTab: new URLSearchParams(searchParams).get("selectedTab"),
      subTab: href,
    });
    setSelectedCategory(href);
  };

  useEffect(() => {
    setIsRequestLoading(true);
    const newItems = SIDE_NAV_ITEMS.filter((item) =>
      checkPermission(item.permissionName, companyAdmin.userPermission)
    );
    setSideNavItems(newItems);

    const subTab = new URLSearchParams(location.search).get("subTab") || "winwire";
    if (!newItems.find((item) => item.href === subTab)) {
      handleChange(newItems[0].href);
    } else {
      setSelectedCategory(subTab);
    }
    setIsRequestLoading(false);
  }, [companyAdmin.userPermission, subscription, location]);

  return (
    <MDBox>
      {isRequestLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <MDBox minWidth={{ xs: "22rem", md: "25rem" }} sx={{ width: "25%" }}>
        <Tabs
          value={sidenavItems.findIndex((tab) => tab.href === selectedCategory)}
          onChange={(e, value) => {
            handleChange(sidenavItems[value].href);
          }}
          aria-label="Settings page insight hub page tab"
        >
          {sidenavItems.map((tab, index) => (
            <Tab label={tab.label} disabled={tab.disabled} {...genTabProps(index)} />
          ))}
        </Tabs>
        {selectedCategory === "insights" ? (
          <Tabs
            sx={{ mt: 1 }}
            value={INSIGHTS_CONFIG_TYPE.findIndex(
              (tab) => tab.value === selectedInsightsConfigType
            )}
            onChange={(e, value) => {
              setSelectedInsightsConfigType(INSIGHTS_CONFIG_TYPE[value].value);
            }}
            aria-label="Settings page insight config type"
          >
            {INSIGHTS_CONFIG_TYPE.map((tab, index) => (
              <Tab label={tab.label} disabled={tab.disabled} {...genTabProps(index)} />
            ))}
          </Tabs>
        ) : null}
      </MDBox>

      <MDBox my={3}>
        <Grid container spacing={3}>
          {selectedCategory === "winwire" && <WinWireSettings />}
          {selectedCategory === "reports" && <ReportsSettings />}
          {selectedCategory === "insights" && (
            <InsightsSettings selectedInsightsConfigType={selectedInsightsConfigType} />
          )}
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default HubSettings;
